import { z } from 'zod';
import { CalendarDate } from './project.js';
/**
 * Issue vận hành — docs/plan-issue-management.md, APM-ISSUE-b.
 *
 * Nguồn sự thật duy nhất cho cả hai đầu. Thứ quan trọng nhất ở file này không
 * phải các schema mà là `ISSUE_PRIORITY_MATRIX`: ưu tiên **suy ra** từ ảnh
 * hưởng × khẩn cấp, và nếu hai đầu mỗi bên giữ một bảng thì form sẽ hiện một ưu
 * tiên còn hàng đợi xếp theo một ưu tiên khác.
 */
/** Ba loại của ITIL 4 — kế hoạch §2.1. */
export const IssueKind = z.enum(['incident', 'request', 'problem']);
/** Bốn loại bảo trì của ISO/IEC/IEEE 14764 — kế hoạch §2.2. */
export const MaintenanceCategory = z.enum(['corrective', 'adaptive', 'perfective', 'preventive']);
export const IssueChannel = z.enum(['portal', 'email', 'phone', 'chat', 'monitoring', 'internal']);
export const IssueState = z.enum([
    'new',
    'triaged',
    'in_progress',
    'waiting_customer',
    'waiting_third_party',
    'resolved',
    'reopened',
    'closed',
]);
// ── Ảnh hưởng × Khẩn cấp → Ưu tiên ──────────────────────────────────────────
/**
 * Thang 1–3 cho cả hai chiều — kế hoạch §3.2.
 *
 * Ba mức chứ không phải năm, và đó là một quyết định về **câu hỏi hỏi người
 * dùng**, không phải về độ chi tiết: ba mức phân biệt được bằng một câu quan
 * sát ("bao nhiêu người gặp?"), còn ở thang năm mức thì mức 2 và mức 3 không có
 * câu hỏi nào tách chúng ra, nên chúng được chọn gần như ngẫu nhiên.
 */
export const IssueScale = z.number().int().min(1).max(3);
/** P1–P4. P1 gấp nhất. */
export const IssuePriority = z.number().int().min(1).max(4);
/**
 * Ma trận ITIL 4, đọc là `[impact][urgency]`.
 *
 * ⚠️ **Đây là nơi duy nhất ưu tiên được quyết.** Giao diện không có ô chọn ưu
 * tiên: có cả ba ô thì mọi issue sẽ là P1 trong vòng một tháng — đó là kết cục
 * ở mọi service desk từng mở ô ấy ra. Ảnh hưởng và khẩn cấp là hai câu **quan
 * sát được**; ưu tiên là hệ quả.
 *
 * Người có `issue:triage` vẫn ghi đè được, nhưng phải kèm lý do thành chữ
 * (`priorityOverrideReason`) — ghi đè im lặng thì bảng này chỉ còn là trang trí.
 */
export const ISSUE_PRIORITY_MATRIX = {
    /** Một người dùng. */
    1: { 1: 4, 2: 4, 3: 3 },
    /** Một nhóm, một phòng ban. */
    2: { 1: 4, 2: 3, 3: 2 },
    /** Toàn bộ người dùng của dịch vụ. */
    3: { 1: 3, 2: 2, 3: 1 },
};
/**
 * Ảnh hưởng × khẩn cấp → ưu tiên.
 *
 * Ném khi ngoài thang thay vì rơi về một mặc định: một giá trị lạ tới đây nghĩa
 * là đã có chỗ nào bỏ qua `IssueScale`, và trả về P4 sẽ giấu đúng lỗi ấy dưới
 * một con số trông hợp lệ.
 */
export function issuePriority(impact, urgency) {
    const row = ISSUE_PRIORITY_MATRIX[impact];
    const value = row?.[urgency];
    if (value === undefined) {
        throw new Error(`Ảnh hưởng/khẩn cấp ngoài thang 1–3: impact=${impact} urgency=${urgency}`);
    }
    return value;
}
/**
 * Ảnh hưởng dùng để tính ưu tiên, theo loại — người dùng duyệt 2026-09-30.
 *
 * Yêu cầu (cấp tài khoản, xin quyền…) là của **một người** theo định nghĩa, nên
 * form không hỏi "bao nhiêu người" và API không nhận số client gửi: nhận thì một
 * yêu cầu tự khai "toàn bộ người dùng" thành P1 và vượt hàng đợi sự cố thật.
 * Người xử lý vẫn sửa được ảnh hưởng sau khi tạo (`UpdateIssueInput`).
 */
export function reportedImpact(kind, impact) {
    return kind === 'request' ? 1 : impact;
}
/**
 * Loại → phân loại bảo trì mặc định — kế hoạch §2.2.
 *
 * Suy mặc định chứ không bắt nhập: một ô phân loại bắt buộc mà người dùng không
 * hiểu sẽ được điền bằng giá trị đầu danh sách, và khi ấy chiều này tệ hơn là
 * không có — nó nói dối mà trông như dữ liệu.
 *
 * `problem` không có mặc định: một nguyên nhân gốc có thể dẫn tới sửa lỗi
 * (`corrective`) hay tới việc đổi kiến trúc cho hết hẳn (`preventive`), và đoán
 * thay người ta ở đây là đoán sai một nửa số lần.
 */
export const DEFAULT_MAINTENANCE_CATEGORY = {
    incident: 'corrective',
    request: 'adaptive',
    problem: null,
};
// ── Vòng đời ────────────────────────────────────────────────────────────────
/**
 * Trạng thái nào đi được sang trạng thái nào.
 *
 * Bảng này ở contract chứ không nằm trong command handler vì giao diện cũng cần
 * nó: nút trạng thái chỉ hiện những bước đi được. Hai bảng ở hai bên sẽ lệch,
 * và khi ấy giao diện mời người dùng bấm một nút mà server từ chối.
 */
export const ISSUE_TRANSITIONS = {
    new: ['triaged', 'in_progress', 'closed'],
    triaged: ['in_progress', 'waiting_customer', 'waiting_third_party', 'closed'],
    in_progress: ['waiting_customer', 'waiting_third_party', 'resolved', 'closed'],
    waiting_customer: ['in_progress', 'resolved', 'closed'],
    waiting_third_party: ['in_progress', 'resolved', 'closed'],
    /* `resolved → reopened` là đường của người báo; `→ closed` là xác nhận. */
    resolved: ['reopened', 'closed'],
    reopened: ['in_progress', 'waiting_customer', 'waiting_third_party', 'resolved'],
    /* Đóng rồi vẫn mở lại được: lỗi tái diễn sau khi đóng là chuyện thường. */
    closed: ['reopened'],
};
export function canTransition(from, to) {
    return ISSUE_TRANSITIONS[from].includes(to);
}
/**
 * Hai trạng thái chờ — kế hoạch §4.2.
 *
 * Đồng hồ SLA **dừng** ở đây; cycle time thì không. Gộp hai đồng hồ làm một là
 * phải chọn giữa phạt đội vì khách đi nghỉ, và báo cho khách một con số nhỏ hơn
 * thứ họ sống qua.
 */
export const ISSUE_WAITING_STATES = ['waiting_customer', 'waiting_third_party'];
/** Chưa xong: còn nằm trên bàn của đội hoặc đang chờ ai đó. */
export const ISSUE_OPEN_STATES = [
    'new',
    'triaged',
    'in_progress',
    'waiting_customer',
    'waiting_third_party',
    'reopened',
];
export function isWaiting(state) {
    return ISSUE_WAITING_STATES.includes(state);
}
export function isOpen(state) {
    return ISSUE_OPEN_STATES.includes(state);
}
/**
 * Lý do chờ — danh sách chọn, không phải ô chữ tự do.
 *
 * Cùng lý lẽ với `task.block_reason` (docs/frontend-guide.md §3): đây là cửa
 * duy nhất tạm dừng đồng hồ SLA, nên nó phải **thống kê được**. Một ô chữ tự do
 * cho ra hai trăm cách viết của cùng ba lý do, và khi ấy không ai trả lời được
 * "thời gian chờ của chúng ta đi đâu".
 */
export const ISSUE_WAIT_REASONS = [
    'need_info',
    'need_approval',
    'customer_testing',
    'vendor_fix',
    'scheduled_window',
    'other',
];
export const IssueWaitReason = z.enum(ISSUE_WAIT_REASONS);
// ── Mục tiêu SLA ────────────────────────────────────────────────────────────
/**
 * Mặc định lúc tạo dự án bảo trì — kế hoạch §3.3. Phút **làm việc**.
 *
 * `null` = ưu tiên ấy không cam kết loại đó, và nó **khác 0**: P4 không có hạn
 * xử lý là một chính sách, không phải một hạn bằng không.
 */
export const DEFAULT_SLA_TARGETS = {
    1: { responseMinutes: 60, resolveMinutes: 4 * 60 },
    2: { responseMinutes: 4 * 60, resolveMinutes: 8 * 60 },
    3: { responseMinutes: 8 * 60, resolveMinutes: 5 * 8 * 60 },
    4: { responseMinutes: 2 * 8 * 60, resolveMinutes: null },
};
export const IssueSlaTarget = z.object({
    priority: IssuePriority,
    responseMinutes: z.number().int().positive().nullable(),
    resolveMinutes: z.number().int().positive().nullable(),
    roundTheClock: z.boolean(),
    effectiveFrom: CalendarDate,
    effectiveTo: CalendarDate.nullable(),
});
export const UpdateSlaTargetsInput = z.object({
    targets: z
        .array(z.object({
        priority: IssuePriority,
        responseMinutes: z.number().int().positive().nullable(),
        resolveMinutes: z.number().int().positive().nullable(),
        roundTheClock: z.boolean().optional(),
    }))
        .min(1)
        .max(4),
});
// ── Input ───────────────────────────────────────────────────────────────────
const IssueBody = {
    title: z.string().trim().min(1).max(500),
    description: z.string().max(20_000).nullable().optional(),
    stepsToReproduce: z.string().max(20_000).nullable().optional(),
    /** Không bao giờ đi ra portal — kế hoạch §8.2 ràng buộc 3. */
    internalNote: z.string().max(20_000).nullable().optional(),
    impact: IssueScale,
    urgency: IssueScale,
    maintenanceCategory: MaintenanceCategory.nullable().optional(),
    assigneeId: z.uuid().nullable().optional(),
    problemId: z.uuid().nullable().optional(),
    reportedByName: z.string().max(200).optional(),
    reportedChannel: IssueChannel.optional(),
};
/**
 * ⚠️ **Không có `priority`, và đó là cửa chặn thật.**
 *
 * Client gửi kèm thì Zod loại bỏ (schema không `passthrough`), nên không có
 * đường nào từ giao diện đặt thẳng ưu tiên — kể cả khi ai đó thêm nhầm một ô
 * vào form sau này.
 */
/**
 * Tạo issue. `typeId` — loại do dự án tự quản (APM-ITYPE-c); `kind` suy từ cách xử
 * lý của loại. Chỉ có `kind` (client cũ, bộ sinh demo) ⇒ loại mặc định của kind ấy.
 * Có cả hai mà lệch nhau ⇒ 400: không đoán hộ bên nào đúng.
 */
export const CreateIssueInput = z
    .object({ kind: IssueKind.optional(), typeId: z.uuid().optional(), ...IssueBody })
    .refine((input) => input.kind !== undefined || input.typeId !== undefined, {
    message: 'Chọn loại issue',
    path: ['typeId'],
});
/**
 * Không có `kind`: đổi loại của một issue đã ghi là xoá dấu vết của nó — số
 * hiệu đã nằm trong email gửi khách. Cùng lý lẽ với `UpdateRaidInput`.
 *
 * `typeId` đổi được, nhưng **chỉ trong cùng cách xử lý** (APM-ITYPE-c): "Sự cố" →
 * "Lỗi giao diện" là phân loại lại, `kind` không đổi. Khác cách xử lý ⇒ 400
 * `ISSUE_TYPE_KIND_MISMATCH` — cùng lý lẽ trên.
 */
export const UpdateIssueInput = z.object({ ...IssueBody, typeId: z.uuid() }).partial();
export const ChangeIssueStateInput = z
    .object({
    state: IssueState,
    /** Bắt buộc khi vào trạng thái chờ — xem `superRefine` dưới. */
    waitReason: IssueWaitReason.optional(),
    note: z.string().max(5_000).optional(),
    /**
     * Lời nhắn **cho người gửi và người theo dõi** — APM-ISX-b. Khác `note` (nội
     * bộ): nó hiện ở lịch sử trạng thái của trang "Issue của tôi" và đi kèm thông
     * báo. `.trim()` trước `.max()`: chuỗi toàn dấu cách không thành lời nhắn rỗng.
     */
    publicNote: z.string().trim().max(2_000).optional(),
})
    .superRefine((value, ctx) => {
    if (isWaiting(value.state) && value.waitReason === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['waitReason'],
            message: 'Phải chọn lý do chờ — đây là cửa duy nhất tạm dừng đồng hồ cam kết',
        });
    }
});
/**
 * Phân loại lại — người có `issue:triage`.
 *
 * `priority` ở đây là **ghi đè**, và nó đi kèm `reason` **bắt buộc**: đó là
 * khác biệt giữa một ngoại lệ có người chịu trách nhiệm và một bảng ma trận
 * không ai tin nữa.
 */
export const TriageIssueInput = z
    .object({
    impact: IssueScale.optional(),
    urgency: IssueScale.optional(),
    priority: IssuePriority.optional(),
    priorityOverrideReason: z.string().max(300).trim().optional(),
    maintenanceCategory: MaintenanceCategory.nullable().optional(),
    assigneeId: z.uuid().nullable().optional(),
})
    .superRefine((value, ctx) => {
    if (value.priority !== undefined && !value.priorityOverrideReason) {
        ctx.addIssue({
            code: 'custom',
            path: ['priorityOverrideReason'],
            message: 'Ghi đè mức ưu tiên phải nói lý do',
        });
    }
});
// ── Output ──────────────────────────────────────────────────────────────────
/** Loại issue gắn trên một issue — APM-ITYPE-c. `color` là hex của bảng màu loại việc. */
export const IssueTypeRef = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
});
export const IssueListItem = z.object({
    id: z.uuid(),
    number: z.number().int().positive(),
    /** `{project.key}-I{number}` — dựng ở server để hai đầu không ghép khác nhau. */
    code: z.string(),
    kind: IssueKind,
    /** `null` = issue chưa được gán loại (tạo trước APM-ITYPE-a) — hiện nhãn theo `kind`. */
    type: IssueTypeRef.nullable(),
    maintenanceCategory: MaintenanceCategory.nullable(),
    title: z.string(),
    state: IssueState,
    impact: IssueScale,
    urgency: IssueScale,
    priority: IssuePriority,
    /** `true` khi ưu tiên không bằng thứ ma trận tính ra. */
    priorityOverridden: z.boolean(),
    assigneeId: z.uuid().nullable(),
    assigneeName: z.string().nullable(),
    reporterId: z.uuid(),
    reporterName: z.string().nullable(),
    reportedByName: z.string(),
    reportedChannel: IssueChannel,
    reportedAt: z.iso.datetime(),
    slaResponseDueAt: z.iso.datetime().nullable(),
    slaResolveDueAt: z.iso.datetime().nullable(),
    firstResponseAt: z.iso.datetime().nullable(),
    resolvedAt: z.iso.datetime().nullable(),
    closedAt: z.iso.datetime().nullable(),
    reopenedCount: z.number().int().nonnegative(),
    /**
     * Tổng phút **làm việc** đã đứng chờ — kế hoạch §4.2.
     *
     * Hiện ngay cạnh thời gian xử lý, không giấu trong báo cáo: một ticket 30
     * ngày mà 28 ngày "chờ khách" thì hoặc đúng thế thật, hoặc là một chỗ đang bị
     * dùng để giấu, và cả hai đều cần nhìn thấy được.
     */
    waitedMinutes: z.number().int().nonnegative(),
    /** Đang chờ từ lúc nào. `null` = không đang chờ. */
    waitingSince: z.iso.datetime().nullable(),
});
export const IssueDetail = IssueListItem.extend({
    description: z.string().nullable(),
    stepsToReproduce: z.string().nullable(),
    /**
     * `null` cho người không được đọc ghi chú nội bộ — **không** phải một trường
     * bị bỏ đi. Bỏ trường thì frontend không phân biệt được "không có ghi chú" với
     * "có mà tôi không được xem", và sẽ hiện ô trống như thể chưa ai viết gì.
     */
    internalNote: z.string().nullable(),
    priorityOverrideReason: z.string().nullable(),
    problemId: z.uuid().nullable(),
    problemCode: z.string().nullable(),
    /** Việc xử lý đã tạo — kế hoạch §5. Rỗng là trạng thái thường gặp nhất. */
    taskIds: z.array(z.uuid()),
});
// ── Nối sang việc ───────────────────────────────────────────────────────────
/**
 * Tạo việc xử lý từ một vấn đề — kế hoạch §5, APM-ISSUE-h.
 *
 * ⚠️ Một chạm, nhưng **không tự động**. Ở dự án bảo trì phần lớn issue không
 * đáng có task: một yêu cầu cấp lại tài khoản mất năm phút, và nếu mỗi ticket đẻ
 * một task thì bảng công việc chìm trong ticket vặt, rồi burndown và velocity —
 * hai chỉ số đang có tác dụng — thành vô dụng ngay tháng đầu.
 */
export const CreateIssueTaskInput = z.object({
    taskTypeId: z.uuid(),
    /** Bỏ trống ⇒ lấy mã và tiêu đề của vấn đề. */
    title: z.string().trim().min(1).max(500).optional(),
    assigneeId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
});
export const LinkIssueTaskInput = z.object({ taskId: z.uuid() });
export const IssueListQuery = z.object({
    state: IssueState.optional(),
    kind: IssueKind.optional(),
    priority: IssuePriority.optional(),
    assigneeId: z.uuid().optional(),
    /** Chỉ việc chưa xong — mặc định của hàng đợi. */
    openOnly: z.boolean().optional(),
    q: z.string().max(200).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    cursor: z.string().max(200).optional(),
});
export const IssueList = z.object({
    items: z.array(IssueListItem),
    nextCursor: z.string().nullable(),
});
// ── Báo cáo vận hành — APM-ISSUE-j, kế hoạch §7 ──────────────────────────────
/**
 * Vào / ra theo tuần. Đường mở trên đường đóng ba tuần liền = tồn đọng chỉ có
 * thể lớn lên và không tự hết — cảnh báo **sớm hơn** mọi chỉ số khác.
 */
export const IssueArrivalBucket = z.object({
    /** Thứ Hai của tuần, `YYYY-MM-DD`. */
    week: CalendarDate,
    created: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
});
/**
 * Phân bố thời gian xử lý theo ưu tiên.
 *
 * ⚠️ **Không có trung bình**, và đó là chủ đích: phân bố thời gian xử lý ticket
 * có đuôi dài rất nặng, nên trung bình luôn nằm ở chỗ không có ticket nào và nó
 * che đúng những cái làm khách hàng gọi điện. `p85Hours` là SLE theo định nghĩa
 * của Kanban Guide — một khoảng thời gian kèm một xác suất.
 */
export const IssueCycleBucket = z.object({
    priority: IssuePriority,
    samples: z.number().int().nonnegative(),
    /** `null` = chưa đóng việc nào ở mức này. Khác 0 ("xong tức thì"). */
    p50Hours: z.number().nonnegative().nullable(),
    p85Hours: z.number().nonnegative().nullable(),
    maxHours: z.number().nonnegative().nullable(),
});
/** Tuân thủ cam kết. Mẫu số **chỉ đếm việc có cam kết** — xem `issue-flow.ts`. */
export const IssueSlaSlice = z.object({
    priority: IssuePriority,
    responseSamples: z.number().int().nonnegative(),
    responseMetPct: z.number().int().min(0).max(100).nullable(),
    resolutionSamples: z.number().int().nonnegative(),
    resolutionMetPct: z.number().int().min(0).max(100).nullable(),
});
/** Tuổi việc đang mở. `upToDays: null` = ô cuối, "hơn ngưỡng trước đó". */
export const IssueAgingBucket = z.object({
    upToDays: z.number().int().positive().nullable(),
    count: z.number().int().nonnegative(),
    /** Trong số đó, bao nhiêu đã quá hạn — hai mức khẩn khác hẳn nhau. */
    overdue: z.number().int().nonnegative(),
});
/** Bảo trì đi vào đâu. `category: null` = chưa phân loại, có ô riêng. */
export const IssueMaintenanceSlice = z.object({
    category: MaintenanceCategory.nullable(),
    count: z.number().int().nonnegative(),
    /** **Giờ quy đổi**, không phải số việc — xem `issue-flow.ts`. */
    hours: z.number().nonnegative(),
});
/**
 * Cả năm biểu đồ trong một lượt gọi.
 *
 * Một endpoint chứ không năm: cả năm đọc **cùng một tập** issue trong cùng một
 * khoảng, nên năm endpoint là năm lần quét cùng dữ liệu cho một màn hình.
 *
 * ⚠️ **Không nhận `asOf`** — luật #7 ngoại lệ 2: `from`/`to` của chính nó *là*
 * bộ điều khiển thời gian, và thêm `asOf` bên cạnh là hai trục thời gian trên
 * một màn hình mà không ai giải thích được cái nào thắng. Riêng `aging` nhìn
 * **hiện tại**, nên nó nhận mốc `asOf` riêng và trả `agingAsOf` để giao diện
 * dán đúng nhãn kỳ.
 */
export const IssueFlowReport = z.object({
    projectId: z.uuid(),
    from: CalendarDate,
    to: CalendarDate,
    arrival: z.array(IssueArrivalBucket),
    cycle: z.array(IssueCycleBucket),
    sla: z.array(IssueSlaSlice),
    aging: z.array(IssueAgingBucket),
    /** Mốc mà cột "tuổi việc đang mở" được chụp — khác `to` của bốn cái kia. */
    agingAsOf: z.iso.datetime(),
    maintenance: z.array(IssueMaintenanceSlice),
});
// ── Mời khách hàng — APM-ISSUE-m, kế hoạch §8.2 ──────────────────────────────
export const InviteClientInput = z.object({
    email: z.email().max(320),
    displayName: z.string().trim().min(1).max(200),
});
/**
 * Kết quả mời.
 *
 * `setPasswordUrl` chỉ có trong **phản hồi này, đúng một lần** — cùng luật với
 * lời mời nhân sự. Token không bao giờ đi vào `event_log`: nó bất biến, và một
 * bí mật ghi vào đó là một bí mật không xoá được.
 *
 * `null` khi email đã có tài khoản: họ **đã** có mật khẩu, và sinh một link đặt
 * lại mật khẩu cho tài khoản người khác — từ tay một PM — là mở đúng cái cửa
 * mà lời mời nhân sự đã đóng lại.
 */
export const InviteClientResult = z.object({
    userId: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    setPasswordUrl: z.string().nullable(),
    expiresAt: z.iso.datetime().nullable(),
});
/** Một khách hàng đang có quyền vào dự án. */
export const ProjectClient = z.object({
    userId: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    /** Số vấn đề người này đã báo — con số duy nhất PM cần khi rà lại danh sách. */
    reportedCount: z.number().int().nonnegative(),
    joinedAt: z.iso.datetime(),
});
/**
 * Người báo mở lại một vấn đề — APM-ISSUE-n.
 *
 * Lý do **bắt buộc**, khác `confirm` (không cần gì). Mở lại nghĩa là đội đã báo
 * xong mà thực ra chưa; không có một câu nói rõ chỗ nào chưa xong thì lượt mở
 * lại ấy chỉ làm `reopened_count` tăng mà không ai biết phải sửa gì.
 */
export const ReopenIssueInput = z.object({
    reason: z.string().trim().min(1).max(2_000),
});
/**
 * Một dự án mà khách hàng vào được — APM-ISSUE-n.
 *
 * Portal cần biết báo vấn đề vào đâu. Trả về **đúng** những trường portal hiện,
 * không phải `ProjectView` đầy đủ: một khách hàng không cần biết phương pháp,
 * ngày bắt đầu, hay ai là PM của dự án — và trả thừa ra là mở một đường rò dữ
 * liệu qua một màn hình không ai soi.
 */
export const PortalProject = z.object({
    projectId: z.uuid(),
    name: z.string(),
    /** Số vấn đề người đang đăng nhập đã báo ở dự án này. */
    reportedCount: z.number().int().nonnegative(),
    openCount: z.number().int().nonnegative(),
});
// ── Người theo dõi — APM-ISX-a, docs/plan-issue-reporter.md §4.1 ─────────────
/**
 * Thêm người theo dõi: nhân viên (có `issue:handle`) chọn theo `userId` từ danh
 * sách thành viên dự án; khách (người gửi) nhập **email** của người đã là thành
 * viên dự án — khách không có danh sách để dò tên người của công ty khác.
 */
export const AddIssueWatcherInput = z.union([
    z.object({ userId: z.string().uuid() }),
    z.object({ email: z.string().trim().toLowerCase().email().max(254) }),
]);
/**
 * Người giao được — APM-ISC-b: thành viên đang hoạt động của dự án **có
 * `issue:handle`** (người dùng chốt 2026-09-30).
 */
export const IssueAssigneeCandidate = z.object({
    userId: z.uuid(),
    displayName: z.string(),
});
export const IssueAssigneeCandidateList = z.object({ items: z.array(IssueAssigneeCandidate) });
export const IssueWatcher = z.object({
    userId: z.string(),
    displayName: z.string(),
    addedAt: z.string(),
    /** Người này là khách hàng (`CLIENT`) — giao diện ghi nhãn "Khách". */
    isClient: z.boolean(),
});
export const IssueWatcherList = z.object({
    items: z.array(IssueWatcher),
    /** Người gọi thêm/bớt được (người gửi hoặc có `issue:handle`). */
    canManage: z.boolean(),
});
// ── Lịch sử công khai — APM-ISX-b, docs/plan-issue-reporter.md §4.2 ──────────
/** Ai làm bước này, theo góc nhìn người gửi: chính họ, đội xử lý, hay hệ thống (job tự đóng). */
export const IssueTimelineActor = z.enum(['reporter', 'team', 'system']);
export const IssueTimelineEntry = z.object({
    at: z.string(),
    kind: z.enum(['created', 'state']),
    /** `null` với `created`. */
    from: IssueState.nullable(),
    to: IssueState,
    actor: IssueTimelineActor,
    /** Tên người làm — **chỉ** trả cho người có `issue:handle`; khách thấy "Đội xử lý". */
    actorName: z.string().nullable(),
    /** Lời nhắn công khai của bước này; ghi chú nội bộ **không bao giờ** ở đây. */
    publicNote: z.string().nullable(),
});
export const IssueTimeline = z.object({ items: z.array(IssueTimelineEntry) });
// ── Trang "Issue của tôi" — APM-ISX-d, docs/plan-issue-reporter.md §4.4 ──────
export const MY_ISSUES_PAGE_SIZE = 50;
export const MyIssuesQuery = z.object({
    /** `reported` = mình gửi, `watching` = được chia sẻ, `all` = cả hai. */
    scope: z.enum(['all', 'reported', 'watching']).default('all'),
    /** `open` = chưa đóng (gồm "đã xử lý, chờ xác nhận"), `closed` = đã đóng. */
    state: z.enum(['open', 'closed', 'all']).default('open'),
    projectId: z.uuid().optional(),
    /** Tìm trong tiêu đề (bỏ dấu) hoặc số issue. */
    q: z.string().trim().max(100).optional(),
    // Mốc + các id cùng mốc đã trả (tối đa 200) — xem `IssueRepository.mine`.
    cursor: z.string().max(10_000).optional(),
});
export const MyIssueItem = IssueListItem.extend({
    projectId: z.uuid(),
    projectName: z.string(),
    /** Vì sao issue này ở trong danh sách của mình. */
    relation: z.enum(['reported', 'watching']),
});
export const MyIssuePage = z.object({
    items: z.array(MyIssueItem),
    /** `null` = hết. Truyền lại nguyên văn vào `cursor`. */
    nextCursor: z.string().nullable(),
});
/**
 * Bộ lọc dự án của trang "Issue của tôi" — APM-ISX-g: chỉ những dự án **có issue
 * khớp các bộ lọc còn lại** (quan hệ, trạng thái, từ khoá). Không nhận
 * `projectId`: dropdown phải liệt kê cả những dự án khác để còn đổi sang được.
 */
export const MyIssueFacetsQuery = MyIssuesQuery.omit({ projectId: true, cursor: true });
export const MyIssueProjectFacet = z.object({
    projectId: z.uuid(),
    key: z.string(),
    name: z.string(),
    /** Số issue khớp bộ lọc ở dự án này. */
    count: z.number().int().positive(),
});
export const MyIssueFacets = z.object({
    /** Xếp theo tên dự án. */
    projects: z.array(MyIssueProjectFacet),
});
/** Dự án người gọi **gửi được** issue (có `issue:report`) — hộp "Gửi issue". */
export const ReportableProject = z.object({
    projectId: z.uuid(),
    key: z.string(),
    name: z.string(),
});
//# sourceMappingURL=issue.js.map